import type { ResolvedNavNode } from '../modules/navigation';

/** Answer of POST /api/search (see backend core/search). */
export interface SearchHit {
  id: string;
  title: string;
  subtitle?: string;
  path: string;
  snippet?: string;
  badge?: string;
}

export interface SearchGroup {
  provider: string;
  label: string;
  icon: string;
  hits: SearchHit[];
  more: boolean;
}

export interface PageHit {
  label: string;
  path: string;
  /** Group the page belongs to, e.g. „Wallbox“. */
  parent?: string;
}

export const MIN_QUERY = 2;

/** Lower case without accents, for comparing labels with the query. */
export function fold(text: string): string {
  return text.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
}

export function queryTerms(query: string): string[] {
  return fold(query).split(/\s+/).filter(Boolean);
}

/** Pages of the person's navigation whose label (or group) contains every word. */
export function matchPages(tree: ResolvedNavNode[], query: string, limit = 4): PageHit[] {
  const terms = queryTerms(query);
  if (terms.length === 0) return [];
  const pages: PageHit[] = tree.flatMap(node => node.kind === 'link'
    ? [{ label: node.label, path: node.path }]
    : node.items.map(item => ({ label: item.label, path: item.path, parent: node.label })));
  const seen = new Set<string>();
  return pages
    .filter(p => {
      const text = fold(`${p.label} ${p.parent ?? ''}`);
      if (!terms.every(t => text.includes(t)) || seen.has(p.path)) return false;
      seen.add(p.path);
      return true;
    })
    .slice(0, limit);
}

/** Splits text into parts, marking those that match a query word. */
export function highlightParts(text: string, query: string): { text: string; match: boolean }[] {
  const terms = queryTerms(query).filter(t => t.length >= 2);
  if (terms.length === 0 || !text) return [{ text, match: false }];
  const folded = fold(text);
  const marks = new Array<boolean>(text.length).fill(false);
  for (const term of terms) {
    let from = 0;
    for (let i = folded.indexOf(term, from); i !== -1; i = folded.indexOf(term, from)) {
      for (let j = i; j < i + term.length && j < marks.length; j++) marks[j] = true;
      from = i + term.length;
    }
  }
  const parts: { text: string; match: boolean }[] = [];
  for (let i = 0; i < text.length; i++) {
    const last = parts[parts.length - 1];
    if (last && last.match === marks[i]) last.text += text[i];
    else parts.push({ text: text[i], match: marks[i] });
  }
  return parts;
}

/** Search query in the address after „#“: never sent to the server, so never in access logs. */
export function searchPageHash(query: string, provider?: string): string {
  const params = new URLSearchParams({ q: query });
  if (provider) params.set('in', provider);
  return `#${params.toString()}`;
}

export function readSearchHash(hash: string): { query: string; provider: string | null } {
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  return { query: params.get('q') ?? '', provider: params.get('in') };
}
